import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
    Text,
    TextInput,
    Tip,
} from 'grommet';
import {
    API,
    ContactService,
    resolveSortField,
    RowsPerPageSelect,
    usePaginatedSort,
} from '@enrich/shared-lib';
import QueryString from 'qs';
import { useDebouncedCallback } from 'use-debounce';
import { useNavigate } from 'react-router-dom';

export const Applicants = () => {
    const [applicants, setApplicants] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    // Bumped after an approval so the effect refetches the current page.
    // Approving moves the contact out of the `applied` filter, so the list
    // must come back from the server or later pages skip whoever shifted up.
    const [reloadToken, setReloadToken] = useState(0);
    const [busyContactIds, setBusyContactIds] = useState([]);
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        setPage,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort({
        initialSort: { property: 'attributes.createdAt', direction: 'desc' },
    });
    const navigate = useNavigate();

    const applySearchTerm = useDebouncedCallback((value) => {
        setAppliedSearchTerm(value);
        setPage(1);
    }, 400);

    useEffect(() => {
        if (!page) return;
        let isActive = true;

        (async () => {
            const response = await loadApplicants({
                page,
                pageSize,
                sort,
                searchTerm: appliedSearchTerm,
            });

            if (!isActive) return;
            setApplicants(response?.data ?? []);
            if (response?.meta?.pagination) {
                applyPagination(response.meta);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [page, pageSize, sort, appliedSearchTerm, reloadToken, applyPagination]);

    const approveApplicant = async (contactId) => {
        setBusyContactIds((current) => [...current, contactId]);
        try {
            await new ContactService().approveContact(contactId);
            setReloadToken((current) => current + 1);
        } catch (error) {
            console.log({ applicantApprovalError: error });
        }
        setBusyContactIds((current) =>
            current.filter((id) => id !== contactId),
        );
    };

    const dataColumns = [
        {
            header: 'Name',
            property: 'attributes.lastName',
            render: (contact) => {
                return (
                    <Anchor
                        key={contact.id}
                        href={`/contacts/${contact.id}`}
                        label={`${contact.attributes.firstName} ${contact.attributes.lastName}`}
                    />
                );
            },
            sortable: true,
        },
        {
            header: 'Graduation',
            property: 'attributes.graduationYear',
            render: (contact) => {
                const semester = contact.attributes.graduationSemester;
                const formattedSemester = semester
                    ? semester[0].toUpperCase() + semester.slice(1)
                    : '';
                return `${formattedSemester} ${contact.attributes.graduationYear}`;
            },
            sortable: true,
        },
        {
            header: 'School',
            property: 'attributes.school.data.attributes.name',
            render: (contact) => {
                return (
                    contact.attributes.school?.data?.attributes?.name || 'N/A'
                );
            },
            sortable: true,
        },
        {
            header: 'Hometown',
            property: 'attributes.hometown',
            sortable: true,
        },
        {
            header: 'Application Date',
            property: 'attributes.createdAt',
            sortable: true,
            render: (contact) => {
                const applicationDate = new Date(contact.attributes.createdAt);
                return applicationDate.toLocaleDateString();
            },
        },
        {
            header: 'Majors',
            property: 'attributes.majors',
            render: (contact) => {
                const majors = contact?.attributes.majors?.data?.map(
                    (major) => major.attributes.name,
                );
                return majors?.length > 0 ? majors.join(', ') : 'N/A';
            },
        },
        {
            header: 'Actions',
            sortable: false,
            render: (contact) => {
                const hasSubmittedDetailedSurvey = Boolean(
                    contact.attributes.detailedSurveySubmittedAt,
                );

                const approveButton = (
                    <Button
                        primary
                        label="Approve"
                        size="small"
                        busy={busyContactIds.includes(contact.id)}
                        disabled={!hasSubmittedDetailedSurvey}
                        onClick={() => approveApplicant(contact.id)}
                    />
                );

                return hasSubmittedDetailedSurvey ? (
                    approveButton
                ) : (
                    <Tip content="Waiting on the detailed application survey">
                        {approveButton}
                    </Tip>
                );
            },
        },
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Box
                    direction="row-responsive"
                    justify="between"
                    align="center">
                    <Heading level="2">Applicants</Heading>
                    <Box direction="row-responsive" align="center" gap="small">
                        <Box width="medium">
                            <TextInput
                                a11yTitle="Search applicants"
                                placeholder="Search by name or school"
                                value={searchTerm}
                                onChange={(event) => {
                                    setSearchTerm(event.target.value);
                                    applySearchTerm(event.target.value);
                                }}
                            />
                        </Box>
                        <Button
                            primary
                            label="View recent approvals"
                            size="large"
                            onClick={() => navigate('/approvals')}
                        />
                    </Box>
                </Box>
                <Box>
                    {applicants.length === 0 ? (
                        <Box pad="medium" align="center">
                            <Text>No applicants found</Text>
                        </Box>
                    ) : (
                        <DataTable
                            primaryKey="id"
                            sort={{ ...sort, external: true }}
                            onSort={handleSort}
                            border="bottom"
                            data={applicants}
                            columns={dataColumns}
                        />
                    )}
                </Box>
                <Box
                    direction="row-responsive"
                    align="center"
                    justify="between"
                    gap="small"
                    pad="small">
                    <RowsPerPageSelect
                        options={stepOptions}
                        value={pageSize}
                        onChange={handlePageSizeChange}
                    />
                    <Pagination
                        numberItems={totalItems}
                        page={page}
                        onChange={handlePageChange}
                        step={pageSize}
                        summary={true}
                    />
                </Box>
            </PageContent>
        </Page>
    );
};

const SORT_FIELD_MAP = {
    'attributes.lastName': 'lastName',
    'attributes.graduationYear': 'graduationYear',
    'attributes.school.data.attributes.name': 'school.name',
    'attributes.hometown': 'hometown',
    'attributes.createdAt': 'createdAt',
};

const loadApplicants = async ({ page, pageSize, sort, searchTerm } = {}) => {
    const sortBy = resolveSortField(sort, SORT_FIELD_MAP, 'createdAt');
    const sortDir = sort?.direction ?? 'desc';
    const term = searchTerm?.trim();
    const query = QueryString.stringify({
        filters: {
            enrichProgramStatus: 'applied',
            // One input, three columns. Strapi joins `school` for the
            // relation filter; it is many-to-one, so no duplicate rows.
            ...(term
                ? {
                      $or: [
                          { firstName: { $containsi: term } },
                          { lastName: { $containsi: term } },
                          { school: { name: { $containsi: term } } },
                      ],
                  }
                : {}),
        },
        populate: ['school', 'majors'],
        pagination: page && pageSize ? { page, pageSize } : undefined,
        // `id:desc` is the tiebreaker: without it, rows sharing a value
        // order non-deterministically under LIMIT/OFFSET and can repeat or
        // vanish across page boundaries.
        sort: [`${sortBy}:${sortDir}`, 'id:desc'],
    });

    try {
        const response = await API.get(`/api/contacts?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return { data: [], meta: { pagination: { total: 0, page } } };
};
